import { useEffect, useState } from "react";
import {
  createGameCoverUploadUrl,
  listStudioAssets,
  uploadToTarget,
} from "../../data/client";
import type { Asset, Game, Tag } from "../../data/types";
import { urlForKey } from "../../library/coverUrl";
import { Button, Display } from "../../design/primitives";
import { CreatorActions } from "./CreatorActions";
import { SlotEditor } from "./SlotEditor";
import { useStudio } from "./StudioContext";
import css from "./studio.module.css";

/**
 * Creator lists - §12, the multi-tenancy contract (D-9).
 *
 * Games, tags and asset types are creator data, never code constants (NFR-13).
 * This is where they are authored.
 *
 * **Retire is never destructive** (D-12). A retired entry leaves the pickers,
 * assets already using it keep their value and keep resolving, restore works,
 * and there is no delete anywhere on this screen.
 */

const newId = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;


/**
 * A game's cover art — `game-cover-art` FR-5, GC-D4.
 *
 * The thumbnail *is* the picker: clicking it chooses a file, and a game that
 * already has art offers REMOVE beneath it. Deliberately not the asset form's
 * slot row, which carries a filename, a progress bar, CANCEL and RETRY — per
 * game across a dozen rows that would triple the height of a screen whose whole
 * value is being scannable.
 *
 * The trade-off, stated rather than discovered: no progress and no cancel for a
 * game cover. At 5 MB on the creator's own connection it is a short wait, and
 * the asset form remains where upload state is treated seriously.
 *
 * **Defined here rather than inside `Lists`.** A component declared inside
 * another is a new component *type* on every parent render, so React unmounts
 * and remounts it rather than updating it — which throws away `busy` and
 * `error` mid-upload and detaches the element from the document. It survives
 * casual use and fails exactly when something is in flight.
 */
function GameArt({
  game,
  baseUrl,
  onChange,
}: {
  game: Game;
  baseUrl: string | undefined;
  onChange: (coverKey: string | undefined) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const url = urlForKey(game.coverKey, baseUrl);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const target = await createGameCoverUploadUrl(game.id, file);
      // The content type is signed into the URL, so a mismatch is refused by S3
      // at the last step - after the upload has looked like it worked.
      await uploadToTarget(target, file, { contentType: file.type });
      onChange(target.key);
    } catch (err) {
      // The server's 422 already names the limit and the accepted types.
      // Repeating that logic here would be a second copy to keep in step.
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label
        className={`${css.gameArt} ${busy ? css.gameArtBusy : ""}`}
        title={`Cover art for ${game.name || "this game"}`}
      >
        {url ? <img src={url} alt="" /> : <span className={css.gameArtEmpty}>{busy ? "…" : "+ ART"}</span>}
        <input
          type="file"
          accept=".png,.jpg,.jpeg,.gif,.webp"
          aria-label={`Cover art for ${game.name || "this game"}`}
          style={{ display: "none" }}
          disabled={busy}
          onChange={(e) => void pick(e.target.files?.[0])}
        />
      </label>
      {url && !busy && (
        <button type="button" className={css.miniButton} onClick={() => onChange(undefined)}>
          REMOVE
        </button>
      )}
      {error && <span className={css.gameArtEmpty}>{error}</span>}
    </div>
  );
}

export default function Lists() {
  const { creator, update } = useStudio();
  const [assets, setAssets] = useState<Asset[]>([]);

  // "In use" counts come from the assets themselves, so retiring something in
  // use is a visible decision rather than a silent one.
  useEffect(() => {
    let live = true;
    listStudioAssets()
      .then((a) => live && setAssets(a))
      .catch(() => live && setAssets([]));
    return () => {
      live = false;
    };
  }, []);

  const gameUse = (id: string) => assets.filter((a) => a.gameId === id).length;
  const tagUse = (id: string) => assets.filter((a) => a.tags.includes(id)).length;

  const setGames = (games: Game[]) => update({ ...creator, games });


  const setTags = (tags: Tag[]) => update({ ...creator, tags });

  return (
    <>
      <CreatorActions />
      <div className={css.head}>
        <Display size={38} as="h1">LISTS</Display>
      </div>

      {/* ---- Games ------------------------------------------------------ */}
      <section className={css.listSection}>
        <Display size={21} as="h2" style={{ letterSpacing: "var(--track-section-header)" }}>
          GAMES
        </Display>
        <p className={css.note}>
          A game owns its franchise and its card tint. Add a hue and the card art
          follows — no artwork is needed for a new game.
        </p>

        <div className={`${css.listRow} ${css.gameRow}`} style={{ borderBottomWidth: 3 }}>
          <span className={css.label}>ART</span>
          <span className={css.label}>NAME</span>
          <span className={css.label}>FRANCHISE</span>
          <span className={css.label}>TINT</span>
          <span className={css.label}>IN USE</span>
          <span />
        </div>

        {creator.games.map((game, i) => (
          <div
            key={game.id}
            className={`${css.listRow} ${css.gameRow} ${game.retired ? css.listRowRetired : ""}`}
          >
            <GameArt
              game={game}
              baseUrl={creator.publicAssetsBaseUrl}
              onChange={(coverKey) => {
                const games = [...creator.games];
                games[i] = { ...game, coverKey };
                setGames(games);
              }}
            />
            <input
              className={css.input}
              value={game.name}
              aria-label="Game name"
              onChange={(e) => {
                const games = [...creator.games];
                games[i] = { ...game, name: e.target.value };
                setGames(games);
              }}
            />
            <input
              className={css.input}
              value={game.franchise}
              aria-label="Franchise"
              placeholder="Zelda"
              onChange={(e) => {
                const games = [...creator.games];
                games[i] = { ...game, franchise: e.target.value };
                setGames(games);
              }}
            />
            <input
              type="color"
              className={css.swatch}
              value={game.tint}
              aria-label="Card tint"
              onChange={(e) => {
                const games = [...creator.games];
                games[i] = { ...game, tint: e.target.value };
                setGames(games);
              }}
            />
            <span className={css.inUse}>
              {gameUse(game.id)} {gameUse(game.id) === 1 ? "ASSET" : "ASSETS"}
            </span>
            <button
              type="button"
              className={css.miniButton}
              onClick={() => {
                const games = [...creator.games];
                games[i] = { ...game, retired: !game.retired };
                setGames(games);
              }}
            >
              {game.retired ? "RESTORE" : "RETIRE"}
            </button>
          </div>
        ))}

        <Button
          variant="secondary"
          size="rail"
          style={{ marginTop: "var(--space-16)" }}
          onClick={() =>
            setGames([
              ...creator.games,
              {
                id: newId("game"),
                name: "",
                franchise: "",
                // A mid-lightness, low-chroma starting hue per §1.3's guidance
                // for new games, so cream and white still read on it.
                tint: "#6FA86A",
                retired: false,
              },
            ])
          }
        >
          + ADD GAME
        </Button>
      </section>

      {/* ---- Tags ------------------------------------------------------- */}
      <section className={css.listSection}>
        <Display size={21} as="h2" style={{ letterSpacing: "var(--track-section-header)" }}>
          TAGS
        </Display>

        {creator.tags.map((tag, i) => (
          <div
            key={tag.id}
            className={`${css.listRow} ${tag.retired ? css.listRowRetired : ""}`}
          >
            <input
              className={css.input}
              value={tag.label}
              aria-label="Tag label"
              onChange={(e) => {
                const tags = [...creator.tags];
                tags[i] = { ...tag, label: e.target.value };
                setTags(tags);
              }}
            />
            <span />
            <span />
            <span className={css.inUse}>
              {tagUse(tag.id)} {tagUse(tag.id) === 1 ? "ASSET" : "ASSETS"}
            </span>
            <button
              type="button"
              className={css.miniButton}
              onClick={() => {
                const tags = [...creator.tags];
                tags[i] = { ...tag, retired: !tag.retired };
                setTags(tags);
              }}
            >
              {tag.retired ? "RESTORE" : "RETIRE"}
            </button>
          </div>
        ))}

        <Button
          variant="secondary"
          size="rail"
          style={{ marginTop: "var(--space-16)" }}
          onClick={() =>
            setTags([...creator.tags, { id: newId("tag"), label: "", retired: false }])
          }
        >
          + ADD TAG
        </Button>
      </section>

      {/* ---- Asset types: file formats only (`editable-file-slots`) ------ */}
      <SlotEditor />

      {/* ---- Landing copy ------------------------------------------------ */}
      <section className={css.listSection}>
        <Display size={21} as="h2" style={{ letterSpacing: "var(--track-section-header)" }}>
          LANDING COPY
        </Display>
        <p className={css.note}>
          Shown to visitors who are not signed in. The arrangement count is
          computed, so it is not written here.
        </p>
        <div style={{ display: "grid", gap: "var(--space-12)", maxWidth: 720 }}>
          <label className={css.label}>
            HEADLINE
            <input
              className={css.input}
              value={creator.landingCopy.headline}
              onChange={(e) =>
                update({
                  ...creator,
                  landingCopy: { ...creator.landingCopy, headline: e.target.value },
                })
              }
            />
          </label>
          <label className={css.label}>
            CADENCE
            <input
              className={css.input}
              value={creator.landingCopy.cadence}
              placeholder="A new one every couple of weeks"
              onChange={(e) =>
                update({
                  ...creator,
                  landingCopy: { ...creator.landingCopy, cadence: e.target.value },
                })
              }
            />
          </label>
          <label className={css.label}>
            DISPLAY NAME — used for “NOTE FROM …” on every tab
            <input
              className={css.input}
              value={creator.displayName}
              onChange={(e) => update({ ...creator, displayName: e.target.value })}
            />
          </label>
        </div>
      </section>
    </>
  );
}
